import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';

import {
  ShippingRoute,
  ShipmentType,
  ShippingRoutePriceRange,
} from './types';

const SHIPPING_ROUTES_QUERY = gql`
{
  shippingRoutes {
    id
    name
    country
    speed
    shippingMethod
    shipmentType
    dimensionalFactor
    isActive
    canGroup
    canDirect
    groupMinWeight
    groupIndividualMinWeight
    priceRanges {
      startWeight
      unitPrice
      basePrice
      roundingWeight
    }
    description
    notes
  }
}
`;

export function useShippingRoutes() {
  return useQuery<
    { shippingRoutes: ShippingRoute[] }
  >(SHIPPING_ROUTES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
}

export function filterShippingRoutesForGroups(shippingRoutes: ShippingRoute[], country: string, type?: ShipmentType) {
  return shippingRoutes.filter((route) =>
    route.country === country &&
    route.isActive &&
    route.canGroup &&
    (!type || route.shipmentType === ShipmentType.Both || route.shipmentType === type));
}

export function filterShippingRoutesForDirect(shippingRoutes: ShippingRoute[], country: string, type?: ShipmentType) {
  return shippingRoutes.filter((route) =>
    route.country === country &&
    route.isActive &&
    route.canDirect &&
    (!type || route.shipmentType === ShipmentType.Both || route.shipmentType === type));
}

export function filterShippingRoutes(shippingRoutes: ShippingRoute[], country: string) {
  return shippingRoutes.filter((route) =>
    route.country === country &&
    route.isActive);
}

export function priceRangeOfShippingRoute(shippingRoute: ShippingRoute, weight: number) {
  let highestPriceRange = shippingRoute.priceRanges[0];
  for (let i = 0; i < shippingRoute.priceRanges.length; i++) {
    const range = shippingRoute.priceRanges[i];
    if (weight >= range.startWeight && highestPriceRange.startWeight < range.startWeight) {
      highestPriceRange = range;
    }
  }
  return highestPriceRange;
}

export function shortPriceDescription(priceRange: ShippingRoutePriceRange) {
  let output = `￥${priceRange.unitPrice}/kg`;
  if (priceRange.basePrice) {
    output = `￥${priceRange.basePrice}+` + output;
  }
  return output;
}

export function shortPriceDescriptionWithoutUnit(priceRange: ShippingRoutePriceRange) {
  let output = `${priceRange.unitPrice}`;
  if (priceRange.basePrice) {
    output = `${priceRange.basePrice}+` + output;
  }
  return output;
}

export function findShippingRoute(shippingRoutes: ShippingRoute[], id: string): ShippingRoute | undefined {
  const matches = shippingRoutes.filter((route) => route.id === id);
  return matches.length > 0 ? matches[0] : undefined;
}

export function explodeShippingRoutesByPrices(shippingRoutes: ShippingRoute[]) {
  return shippingRoutes.map((route) => {
    const firstRangeAboveMinWeight = route.priceRanges.findIndex((priceRange) => priceRange.startWeight > route.groupMinWeight);
    let startIndex;
    if (firstRangeAboveMinWeight < 0) {
      // If no range is higher than min weight, then only the highest can be used
      startIndex = route.priceRanges.length - 1;
    } else if (firstRangeAboveMinWeight > 0) {
      // Start with the last one lower than min weight
      startIndex = firstRangeAboveMinWeight - 1;
    } else {
      startIndex = 0;
    }
    return route.priceRanges.slice(startIndex).map((priceRange) => {
      return {
        weight: Math.max(priceRange.startWeight, route.groupMinWeight),
        route,
        priceRange,
      }
    })
  }).flat();
}
